import { Box, Button, Modal, Typography } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// If this file sits directly in src/components/, change to "../utils/ErrorModal"
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// Plan pill colours — visual only, the subscription value is unchanged.
const PLAN_TONE = {
  FREE: { bg: "#F1F4F8", edge: LINE, ink: INK_2 },
  BASIC: { bg: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82" },
  PRO: { bg: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF" },
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  maxWidth: "92vw",
  backgroundColor: "#fff",
  borderRadius: "18px",
  boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
  padding: "20px 22px",
  outline: "none",
  fontFamily: FONT,
  color: INK,
};

const btnBase = {
  height: 36,
  px: 1.75,
  borderRadius: "9px !important",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
};
const primaryBtnSx = {
  ...btnBase,
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  "&:hover": { background: "#084767 !important" },
  "&.Mui-disabled": { opacity: 0.6 },
};
const outlineBtnSx = {
  ...btnBase,
  background: "#FFFFFF !important",
  color: `${INK} !important`,
  border: `1px solid ${FIELD_EDGE} !important`,
  "&:hover": { background: "#F4F7FA !important" },
  "&.Mui-disabled": { opacity: 0.6 },
};
const rowBtnSx = {
  minWidth: 0,
  height: 28,
  px: 1.25,
  margin: "0 !important",
  borderRadius: "8px !important",
  fontFamily: FONT,
  fontSize: "12.5px !important",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
};

const AllOrgs = () => {
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const getOrgs = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/organization`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );
    setOrgs(res.data);
  };

  useEffect(() => {
    getOrgs();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, date] = dateString.split("-");
    return `${date}/${month}/${year}`;
  };

  const handleVerifyClick = async (orgId) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/payment?orgId=${orgId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setPayment(res.data);
      setOpen(true);
    } catch (err) {
      showModal(err.response?.data?.message || "No pending payment found", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/payment?paymentId=${payment.paymentId}`,
        {},
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      showModal("Payment verified successfully", "success");
      setOpen(false);
      getOrgs();
    } catch (err) {
      showModal("Failed to verify payment", "error");
    }
  };

  const columns = [
    {
      field: "orgId",
      headerName: "Org ID",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>{params.value}</span>
      ),
    },
    {
      field: "orgName",
      headerName: "Org Name",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontWeight: 700, color: INK }}>{params.value}</span>
      ),
    },
    {
      field: "adminEmail",
      headerName: "Admin Email",
      flex: 1,
      renderCell: (params) => (
        <span style={{ color: INK_2 }}>{params.value}</span>
      ),
    },
    {
      field: "adminMobile",
      headerName: "Admin Mobile",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>
          {params.value
            ? params.row.countryCode != null
              ? `${params.row.countryCode}${params.value}`
              : `+${params.value}`
            : "-"}
        </span>
      ),
    },
    {
      field: "subscriptionStartDate",
      headerName: "Subscription Start Date",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>
          {formatDate(params.row.subscriptionStartDate)}
        </span>
      ),
    },
    {
      field: "subscriptionEndDate",
      headerName: "Subscription End Date",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>
          {formatDate(params.row.subscriptionEndDate)}
        </span>
      ),
    },
    {
      field: "subscription",
      headerName: "Subscription",
      flex: 1,
      renderCell: (params) => {
        const tone = PLAN_TONE[params.value] || PLAN_TONE.FREE;
        return params.value ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.125,
              py: 0.25,
              borderRadius: "999px",
              background: tone.bg,
              border: `1px solid ${tone.edge}`,
              color: tone.ink,
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.05em",
              lineHeight: 1.5,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Box>
        ) : null;
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 2.5,
      renderCell: (params) => (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, height: "100%" }}>
          <Button
            className="btn-contained"
            sx={{
              ...rowBtnSx,
              width: "auto",
              background: "#FFFFFF !important",
              color: `${INK} !important`,
              border: `1px solid ${FIELD_EDGE} !important`,
              "&:hover": { background: "#F4F7FA !important" },
            }}
            onClick={() =>
              navigate(`/superadmin-dashboard/${params.row.orgId}`)
            }
          >
            Edit Org
          </Button>
          <Button
            className="btn-contained"
            sx={{
              ...rowBtnSx,
              width: "auto",
              background: "#FFFFFF !important",
              color: `${INK} !important`,
              border: `1px solid ${FIELD_EDGE} !important`,
              "&:hover": { background: "#F4F7FA !important" },
            }}
            onClick={() =>
              navigate(`/superadmin-dashboard/${params.row.orgId}/admin`)
            }
          >
            Edit Admin
          </Button>
          <Button
            className="btn-contained"
            sx={{
              ...rowBtnSx,
              width: "auto",
              background: "#E9F7EE !important",
              color: "#1B6B41 !important",
              border: "1px solid #C4E8D2 !important",
              "&:hover": { background: "#DCF1E4 !important" },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
            onClick={() => handleVerifyClick(params.row.orgId)}
            disabled={loading}
          >
            Verify
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: FONT, color: INK }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h1
          style={{
            margin: "8px 0 0",
            fontFamily: FONT,
            fontSize: 27,
            fontWeight: 800,
            letterSpacing: "-0.028em",
            color: INK,
          }}
        >
          List of Organizations
        </h1>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "end", columnGap: 8, rowGap: 8 }}>
          <Button
            className="btn-contained"
            sx={outlineBtnSx}
            onClick={() => navigate("/superadmin-dashboard/meta-status")}
          >
            + Add Lead Status
          </Button>
          <Button
            className="btn-contained"
            sx={outlineBtnSx}
            onClick={() => navigate("/superadmin-dashboard/features")}
          >
            + Features
          </Button>
          <Button
            className="btn-contained"
            sx={primaryBtnSx}
            onClick={() => navigate("/superadmin-dashboard/create-org")}
          >
            + Create Org
          </Button>
        </div>
      </div>
      <div className="dashboard-container" style={{ padding: 0, background: "transparent", boxShadow: "none" }}>
        <Box
          sx={{
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={orgs}
            columns={columns}
            pageSizeOptions={[10]}
            getRowId={(row) => row.orgId}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            rowHeight={50}
            columnHeaderHeight={40}
            sx={{
              border: "none",
              fontFamily: `${FONT} !important`,
              color: INK,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontFamily: FONT,
                fontWeight: "700 !important",
                fontSize: 10.5,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: MUTED,
              },
              "& .MuiDataGrid-columnHeaders": {
                borderTop: `1px solid ${LINE}`,
                borderBottom: `1px solid ${LINE}`,
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler": {
                background: "#F7F9FC !important",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13.5px",
                border: "none",
                borderBottom: "1px solid #F1F4F8",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${LINE}` },
              "& .MuiTablePagination-root, & .MuiTablePagination-displayedRows": {
                fontFamily: MONO,
                fontSize: 12,
                color: MUTED,
              },
              [`& .${gridClasses.row}.even`]: {
                backgroundColor: "#FFFFFF",
              },
              [`& .${gridClasses.row}.odd`]: {
                backgroundColor: "#FCFDFE",
              },
              [`& .${gridClasses.row}.even:hover, & .${gridClasses.row}.odd:hover`]: {
                backgroundColor: "#F4F7FA",
              },
            }}
          />
        </Box>
      </div>

      {/* VERIFY PAYMENT MODAL */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            mb={2}
            sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}
          >
            Verify Payment
          </Typography>

          <Box
            sx={{
              background: "#F7F9FC",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {payment?.receipt ? (
              payment?.receipt?.endsWith(".pdf") ? (
                <iframe
                  src={payment.receipt}
                  title="Receipt PDF"
                  width="100%"
                  height="400px"
                  style={{ display: "block", border: 0 }}
                />
              ) : (
                <img
                  src={payment?.receipt}
                  alt="Receipt"
                  style={{ display: "block", width: "100%", maxHeight: 400, objectFit: "contain" }}
                />
              )
            ) : (
              <p style={{ margin: 0, padding: "28px 16px", textAlign: "center", fontFamily: FONT, fontSize: 13.5, color: MUTED }}>
                No receipt available
              </p>
            )}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}
          >
            <Button variant="outlined" onClick={() => setOpen(false)} sx={outlineBtnSx}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              disabled={!payment?.receipt}
              onClick={handleVerifyPayment}
              sx={{
                ...primaryBtnSx,
                "&.Mui-disabled": { background: "#C3CDD9 !important", color: "#FFFFFF !important" },
              }}
            >
              Verify
            </Button>
          </Box>
        </Box>
      </Modal>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />
    </div>
  );
};

export default AllOrgs;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end", px: 1.75, py: 1.25 }}>
      <GridToolbarQuickFilter
        sx={{
          width: 300,
          border: "1px solid #D5DEE7",
          borderRadius: "9px",
          padding: "3px 10px",
          background: "#FFFFFF",
          "&:focus-within": { borderColor: PRIMARY },
          "& .MuiInputBase-root": { fontFamily: FONT, fontSize: 13.5 },
          "& .MuiInputBase-root::before, & .MuiInputBase-root::after": { display: "none" },
          "& .MuiSvgIcon-root": { color: MUTED, fontSize: 18 },
        }}
      />
    </GridToolbarContainer>
  );
}